import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiAward, FiCheckCircle } from 'react-icons/fi';
import SEO from '../components/SEO';
import HeroSection from '../components/HeroSection';
import RichTextRenderer from '../components/RichTextRenderer';
import MasonryGallery from '../components/MasonryGallery';
import GalleryLightbox from '../components/GalleryLightbox';
import { getTeam, getAwards } from '../services/dataService';

const AboutPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [awards, setAwards] = useState([]);
  const [awardsCategories, setAwardsCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loadingAwards, setLoadingAwards] = useState(true);

  // Fetch team and awards data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch team data
        const teamData = await getTeam();
        setTeamMembers(teamData);

        // Fetch all awards data
        const awardsData = await getAwards();
        setAwards(awardsData);

        // Extract unique categories
        const categories = [...new Set(awardsData.map(award => award.category))];
        setAwardsCategories(categories);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
        setLoadingAwards(false);
      }
    };

    fetchData();
  }, []);

  // Separate leadership and expert team based on sort order
  const leadership = teamMembers.filter(member => member.sortOrder <= 2);
  const expertTeam = teamMembers.filter(member => member.sortOrder > 2);

  // Filter awards by selected category
  const filteredAwards = selectedCategory === 'all' 
    ? awards 
    : awards.filter(award => award.category === selectedCategory);

  // Reset currentImageIndex when category changes to prevent out-of-bounds error
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedCategory]);

  // Group awards by category for display
  const awardsByCategory = awardsCategories.reduce((acc, category) => {
    acc[category] = awards.filter(award => award.category === category);
    return acc;
  }, {});

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setSelectedImage(true);
  };

  const closeLightbox = () => {
    setSelectedImage(false);
  };

  const nextImage = () => {
    const newIndex = (currentImageIndex + 1) % galleryImages.length;
    setCurrentImageIndex(newIndex);
  };

  const prevImage = () => {
    const newIndex = (currentImageIndex - 1 + galleryImages.length) % galleryImages.length;
    setCurrentImageIndex(newIndex);
  };

  // Transform awards data for gallery component - recalculates when filteredAwards changes
  const galleryImages = filteredAwards.map(award => ({
    id: award.id,
    url: award.image,
    title: award.title,
    description: award.description,
    category: award.category
  }));

  return (
    <div className="bg-gradient-to-b from-white via-emerald-50/30 to-white">
      <SEO
        pageType="about"
        title="About Dr. Saurabh Singh | JIVA NADI Therapy Center | 15+ Years Experience"
        description="Meet Dr. Saurabh Singh, Mumbai's leading chiropractor with 15+ years of experience. Certified in chiropractic care, acupuncture, and holistic therapies."
        keywords="Dr. Saurabh Singh, chiropractor Mumbai, acupuncture specialist, holistic healing, pain relief expert, JIVA NADI"
      />

      {/* Reusable Hero Section */}
      <HeroSection
        title="About Us"
        subtitle="Leading Healthcare Excellence"
        description="Welcome to our About Us page, where we explore the healing power of Nadi Therapy and Chiropractic Care. Discover how our dedicated team of professionals combine ancient wisdom with modern techniques to restore balance and promote overall well-being."
        image="https://images.unsplash.com/photo-1565307528294-f70f3c7094e0?w=800&h=600&fit=crop"
        badge="Leading Healthcare Excellence"
        trustIndicators={[
          { value: "15+", label: "Years Experience" },
          { value: "50K+", label: "Happy Patients" },
          { value: "100%", label: "Natural Care" }
        ]}
      />

      {/* Leadership Team Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-6xl font-black text-gray-900 mb-4">
              Meet Our <span className="text-emerald-600">Leaders</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Pioneering healthcare professionals dedicated to your wellness journey
            </p>
          </motion.div>

          <div className="space-y-32">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : leadership.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-500">No leadership team members found.</p>
              </div>
            ) : (
              leadership.map((doctor, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-16 items-center`}
              >
                {/* Image Section */}
                <div className="w-full lg:w-5/12">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    className="relative group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-3xl transform rotate-3 group-hover:rotate-6 transition-transform duration-300"></div>
                    <div className="relative overflow-hidden rounded-3xl shadow-2xl">
                      <img
                        src={doctor.image}
                        alt={doctor.name}
                        onError={(e) => {
                          if(doctor.name.includes("Saurabh")) e.target.src = "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=800&h=1000&fit=crop";
                        }}
                        className="w-full aspect-[4/5] object-cover transform group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent"></div>
                    </div>
                  </motion.div>
                </div>

                {/* Content Section */}
                <div className="w-full lg:w-7/12 space-y-6">
                  <div>
                    <motion.h3
                      initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      className="text-4xl lg:text-5xl font-black text-gray-900 mb-3"
                    >
                      {doctor.name}
                    </motion.h3>
                    <p className="text-xl lg:text-2xl font-semibold text-emerald-600 mb-6">{doctor.role}</p>
                  </div>

                  <div className="prose prose-lg max-w-none">
                    <RichTextRenderer content={doctor.bio} className="mb-4" />
                  </div>

                  {/* Skills/Specialties */}
                  {doctor.skills && doctor.skills.length > 0 && (
                    <div className="pt-6">
                      <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <FiCheckCircle className="text-emerald-600" />
                        Areas of Expertise
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {doctor.skills.map((skill, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                            className="flex items-center gap-3 bg-emerald-50 px-4 py-3 rounded-xl border border-emerald-100"
                          >
                            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                            <span className="text-sm font-medium text-gray-800">{skill}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Qualifications */}
                  {doctor.qualifications && (
                    <div className="pt-4">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Qualifications</h4>
                      <p className="text-gray-600">{doctor.qualifications}</p>
                    </div>
                  )}

                  {/* Languages */}
                  {doctor.languages && doctor.languages.length > 0 && (
                    <div className="pt-4">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">Languages</h4>
                      <p className="text-gray-600">{doctor.languages.join(', ')}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )))}
          </div>
        </div>
      </section>

      {/* Expert Team Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-6xl font-black text-gray-900 mb-4">
              Our Team of <span className="text-emerald-600">Expert Doctors</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Renowned specialists committed to delivering exceptional holistic healthcare
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {loading ? (
              <div className="col-span-full flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : expertTeam.length === 0 ? (
              <div className="col-span-full text-center py-20">
                <p className="text-gray-500">No expert team members found.</p>
              </div>
            ) : (
              expertTeam.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                <div className="flex flex-col sm:flex-row gap-6 p-6 lg:p-8">
                  {/* Image */}
                  <div className="flex-shrink-0">
                    <div className="relative w-32 h-32 sm:w-40 sm:h-40 mx-auto sm:mx-0">
                      <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl transform rotate-3 group-hover:rotate-6 transition-transform duration-300"></div>
                      <img
                        src={member.image}
                        alt={member.name}
                        className="relative w-full h-full object-cover rounded-2xl shadow-lg"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 space-y-3 text-center sm:text-left">
                    <div>
                      <h4 className="text-xl lg:text-2xl font-black text-gray-900 mb-1">
                        {member.name}
                      </h4>
                      <p className="text-emerald-600 font-bold text-sm uppercase tracking-wide mb-2">
                        {member.role}
                      </p>
                      <p className="text-xs font-medium text-gray-500 bg-gray-50 inline-block px-3 py-1 rounded-full">
                        {member.qualifications || member.role}
                      </p>
                    </div>

                    <RichTextRenderer content={member.bio} className="text-sm" />

                    {/* Skills/Specialties */}
                    {member.skills && member.skills.length > 0 && (
                      <div className="pt-2">
                        <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                          {member.skills.slice(0, 3).map((skill, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full"
                            >
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Languages */}
                    {member.languages && member.languages.length > 0 && (
                      <p className="text-xs text-gray-500 italic pt-2">
                        <span className="font-semibold">Languages:</span> {member.languages.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            )))}
          </div>
        </div>
      </section>

      {/* Certificates & Awards Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-6xl font-black text-gray-900 mb-4">
              Certifications & <span className="text-emerald-600">Recognition</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence validated by prestigious certifications and awards
            </p>
          </motion.div>

          {/* Category Tabs - Clean display without counts */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                selectedCategory === 'all'
                  ? 'bg-emerald-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {awardsCategories.map((category) => {
              const displayName = category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-emerald-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {displayName}
                </button>
              );
            })}
          </div>
          {/* Awards Grid - Using MasonryGallery Component */}
          {loadingAwards ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
          ) : filteredAwards.length === 0 ? (
            <div className="text-center py-20">
              <FiAward className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No awards found in this category.</p>
            </div>
          ) : (
            <>
              <MasonryGallery 
                images={galleryImages}
                onImageClick={openLightbox}
              />
            </>
          )}
        </div>
      </section>

      {/* Gallery Lightbox - Using GalleryLightbox Component */}
      <GalleryLightbox
        images={galleryImages}
        currentIndex={currentImageIndex}
        isOpen={selectedImage}
        onClose={closeLightbox}
        onNext={nextImage}
        onPrev={prevImage}
      />
    </div>
  );
};

export default AboutPage;